import React, { useEffect, useMemo, useState } from "react";
import { updateDoc, doc } from "firebase/firestore";
import { db } from "../firebaseConfig";
import { useMonthlyReportData } from "../hooks/useMonthlyReportData";
import "../css/MonthlyReport.css";

const monthNames = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];
// Hoisted helper so it's safe to use anywhere below
function estimateKidCount(playCost) {
  if (!playCost || playCost <= 0) return 0;
  const prices = [30, 25, 20];
  let count = 0;
  let remaining = playCost;

  for (const p of prices) {
    if (remaining >= p) {
      remaining -= p;
      count++;
    } else {
      break;
    }
  }
  count += Math.floor(remaining / 20);
  return count;
}

function toCSV(rows, headers) {
  const escape = (v) =>
    `"${String(v ?? "")
      .replaceAll(`"`, `""`)
      .replaceAll(/\r?\n/g, " ")}"`;
  const head = headers.map(escape).join(",");
  const body = rows
    .map((r) => headers.map((h) => escape(r[h])).join(","))
    .join("\n");
  return [head, body].join("\n");
}

const MonthlyReport = () => {
  const now = new Date();

  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [reportType, setReportType] = useState("transactions"); // "transactions" | "privateEvents"

  const { transactions, privateEvents, setPrivateEvents, loading, err } =
    useMonthlyReportData(month, year);
  const [search, setSearch] = useState("");

  // client-side sort state
  const [sortKey, setSortKey] = useState(""); // e.g. "Date" or "startTime"
  const [sortDir, setSortDir] = useState("asc"); // "asc" | "desc"

  const [showTop, setShowTop] = useState(false);

  useEffect(() => {
    const onScroll = () => setShowTop(window.scrollY > 300);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

  const activeData = reportType === "transactions" ? transactions : privateEvents;

  // Quick navigate helpers
  const prevMonth = () => {
    setMonth((m) => {
      if (m === 1) {
        setYear((y) => y - 1);
        return 12;
      }
      return m - 1;
    });
  };
  const nextMonth = () => {
    setMonth((m) => {
      if (m === 12) {
        setYear((y) => y + 1);
        return 1;
      }
      return m + 1;
    });
  };
  const thisMonth = () => {
    const n = new Date();
    setMonth(n.getMonth() + 1);
    setYear(n.getFullYear());
  };

  // Client-side filter by search text (name/phone/package/etc.)
  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    if (!s) return activeData;

    if (reportType === "transactions") {
      return activeData.filter((t) => {
        const foods = (t.FoodOrder || [])
          .map((f) => `${f.food || ""} x${f.quantity || 0}`)
          .join(" ");
        const hay = [
          t.PName,
          t.PhoneNum,
          t.Date,
          t.PayMethod,
          foods,
          String(t.TotalAmount ?? ""),
          String(t.KidCount ?? ""),
        ]
          .join(" ")
          .toLowerCase();
        return hay.includes(s);
      });
    }
    // privateEvents
    return activeData.filter((e) => {
      const hay = [
        e.firstName,
        e.lastName,
        e.phoneNumber,
        e.packageType,
        e.deposit,
        String(e.depositAmount ?? ""),
        String(e.totalPaidAmount ?? ""),
        e.paymentMethod,
      ]
        .join(" ")
        .toLowerCase();
      return hay.includes(s);
    });
  }, [activeData, search, reportType]);

  // Client-side sort (click column headers)
  const sorted = useMemo(() => {
    if (!sortKey) return filtered;
    const copy = [...filtered];
    copy.sort((a, b) => {
      const av = a[sortKey];
      const bv = b[sortKey];

      // If field looks like date/time, compare as Date
      const isDateKey =
        sortKey.toLowerCase().includes("date") ||
        sortKey.toLowerCase().includes("time");

      let cmp;
      if (isDateKey) {
        cmp = new Date(av) - new Date(bv);
      } else if (typeof av === "number" && typeof bv === "number") {
        cmp = av - bv;
      } else {
        cmp = String(av ?? "").localeCompare(String(bv ?? ""));
      }
      return sortDir === "asc" ? cmp : -cmp;
    });
    return copy;
  }, [filtered, sortKey, sortDir]);

  // Totals (memoized)
  const totals = useMemo(() => {
    if (reportType === "transactions") {
      const totalCustomers = sorted.length;
      const totalAmount = sorted.reduce((s, i) => s + (i.TotalAmount || 0), 0);
      const totalKids = sorted.reduce(
        (s, i) => s + (i.KidCount ?? estimateKidCount(i.PlayCost || 0)),
        0
      );
      const totalCash = sorted.reduce(
        (s, i) => s + (i.TotalCashAmount || 0),
        0
      );
      const totalCard = sorted.reduce(
        (s, i) => s + (i.TotalCardAmount || 0),
        0
      );
      return { totalCustomers, totalKids, totalAmount, totalCash, totalCard };
    }
    // privateEvents
    const totalPrivateEvents = sorted.length;
    const totalPrivateEventsAmount = sorted.reduce(
      (s, i) => s + (i.totalPaidAmount || 0) + (i.depositAmount || 0),
      0
    );
    const totalPrivateEventsCash = sorted.reduce(
      (s, i) =>
        s +
        (i.paymentMethod === "Cash"
          ? (i.totalPaidAmount || 0) + (i.depositAmount || 0)
          : 0),
      0
    );
    const totalPrivateEventsCard = sorted.reduce(
      (s, i) =>
        s +
        (i.paymentMethod === "Card"
          ? (i.totalPaidAmount || 0) + (i.depositAmount || 0)
          : 0),
      0
    );
    return {
      totalPrivateEvents,
      totalPrivateEventsAmount,
      totalPrivateEventsCash,
      totalPrivateEventsCard,
    };
  }, [sorted, reportType]);

  // const estimateKidCount = (playCost) => {
  //   if (!playCost || playCost <= 0) return 0;
  //   const prices = [30, 25, 20];
  //   let count = 0;
  //   let remaining = playCost;
  //   for (const p of prices) {
  //     if (remaining >= p) {
  //       remaining -= p;
  //       count++;
  //     } else break;
  //   }
  //   count += Math.floor(remaining / 20);
  //   return count;
  // };

  // inline updates for Private Events
  const togglePaidStatus = async (id, current) => {
    const newStatus = !current;
    try {
      await updateDoc(doc(db, "privateEvents", id), { paidInFull: newStatus });
      setPrivateEvents((prev) =>
        prev.map((i) => (i.id === id ? { ...i, paidInFull: newStatus } : i))
      );
    } catch (e) {
      console.error("Error updating paid status:", e);
    }
  };

  const updateEventPaymentMethod = async (id, method) => {
    try {
      await updateDoc(doc(db, "privateEvents", id), { paymentMethod: method });
      setPrivateEvents((prev) =>
        prev.map((i) => (i.id === id ? { ...i, paymentMethod: method } : i))
      );
    } catch (e) {
      console.error("Error updating payment method:", e);
    }
  };

  const updatePaidAmount = async (id, amount) => {
    try {
      await updateDoc(doc(db, "privateEvents", id), {
        totalPaidAmount: amount,
      });
      setPrivateEvents((prev) =>
        prev.map((i) => (i.id === id ? { ...i, totalPaidAmount: amount } : i))
      );
    } catch (e) {
      console.error("Error updating paid amount:", e);
    }
  };

  const setSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  const exportCSV = () => {
    if (!sorted.length) return;
    if (reportType === "transactions") {
      const headers = [
        "#",
        "PName",
        "PhoneNum",
        "Date",
        "Time",
        "KidCount",
        "PlayCost",
        "TotalAmount",
      ];
      const rows = sorted.map((i, idx) => ({
        "#": idx + 1,
        PName: i.PName ?? "",
        PhoneNum: i.PhoneNum ?? "",
        Date: i.Date ?? "",
        Time: i.Time ?? "",
        KidCount: i.KidCount ?? estimateKidCount(i.PlayCost || 0),
        PlayCost: (i.PlayCost ?? 0).toFixed(2),
        TotalAmount: (i.TotalAmount ?? 0).toFixed(2),
      }));
      const csv = toCSV(rows, headers);
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Monthly_${monthNames[month - 1]}_${year}_transactions.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } else {
      const headers = [
        "#",
        "firstName",
        "lastName",
        "phoneNumber",
        "startTime",
        "endTime",
        "numberOfKids",
        "numberOfGuests",
        "packageType",
        "depositAmount",
        "paidInFull",
        "paymentMethod",
        "totalPaidAmount",
      ];
      const rows = sorted.map((i, idx) => ({
        "#": idx + 1,
        firstName: i.firstName ?? "",
        lastName: i.lastName ?? "",
        phoneNumber: i.phoneNumber ?? "",
        startTime: i.startTime ?? "",
        endTime: i.endTime ?? "",
        numberOfKids: i.numberOfKids ?? 0,
        numberOfGuests: i.numberOfGuests ?? 0,
        packageType: i.packageType ?? "",
        depositAmount: (i.depositAmount ?? 0).toFixed(2),
        paidInFull: i.paidInFull ? "Yes" : "No",
        paymentMethod: i.paymentMethod ?? "",
        totalPaidAmount: (i.totalPaidAmount ?? 0).toFixed(2),
      }));
      const csv = toCSV(rows, headers);
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Monthly_${monthNames[month - 1]}_${year}_privateEvents.csv`;
      a.click();
      URL.revokeObjectURL(url);
    }
  };

  return (
    <div className="monthly-report-container">
      <div className="monthly-report-header">
        <h2 className="monthly-report-title">Monthly Report</h2>

        <div className="header-actions">
          <button
            className="btn ghost"
            onClick={prevMonth}
            aria-label="Previous month"
          >
            ◀
          </button>
          <div className="filters">
            <select
              value={month}
              onChange={(e) => setMonth(Number(e.target.value))}
              aria-label="Month"
            >
              {monthNames.map((name, idx) => (
                <option key={name} value={idx + 1}>
                  {name}
                </option>
              ))}
            </select>
            <input
              type="number"
              min="2000"
              max={new Date().getFullYear() + 1}
              value={year}
              onChange={(e) => setYear(Number(e.target.value))}
              aria-label="Year"
            />
            <select
              value={reportType}
              onChange={(e) => setReportType(e.target.value)}
              aria-label="Report type"
            >
              <option value="transactions">Daily Transactions</option>
              <option value="privateEvents">Private Events</option>
            </select>
            <input
              className="search"
              type="text"
              placeholder="Search name, phone, package…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              aria-label="Search"
            />
          </div>
          <button
            className="btn ghost"
            onClick={nextMonth}
            aria-label="Next month"
          >
            ▶
          </button>
        </div>

        <div className="bar-actions">
          <button className="btn" onClick={thisMonth}>
            This Month
          </button>
          <button className="btn" onClick={exportCSV}>
            Export CSV
          </button>
          <button className="btn" onClick={() => window.print()}>
            Print
          </button>
        </div>
      </div>

      {loading ? (
        <div className="loading">Loading…</div>
      ) : err ? (
        <div className="error">{err}</div>
      ) : (
        <>
          {sorted.length > 0 ? (
            <>
              {/* KPI strip */}
              <div className="kpis">
                {reportType === "transactions" ? (
                  <>
                    <div className="kpi">
                      <div className="kpi-label">Customers</div>
                      <div className="kpi-value">{totals.totalCustomers}</div>
                    </div>
                    <div className="kpi">
                      <div className="kpi-label">Kids</div>
                      <div className="kpi-value">{totals.totalKids}</div>
                    </div>
                    <div className="kpi">
                      <div className="kpi-label">Cash</div>
                      <div className="kpi-value">
                        ${totals.totalCash.toFixed(2)}
                      </div>
                    </div>
                    <div className="kpi">
                      <div className="kpi-label">Card</div>
                      <div className="kpi-value">
                        ${totals.totalCard.toFixed(2)}
                      </div>
                    </div>
                    <div className="kpi">
                      <div className="kpi-label">Total</div>
                      <div className="kpi-value">
                        ${totals.totalAmount.toFixed(2)}
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="kpi">
                      <div className="kpi-label">Private Events</div>
                      <div className="kpi-value">
                        {totals.totalPrivateEvents}
                      </div>
                    </div>
                    <div className="kpi">
                      <div className="kpi-label">Amount (Dep + Paid)</div>
                      <div className="kpi-value">
                        ${totals.totalPrivateEventsAmount.toFixed(2)}
                      </div>
                    </div>
                    <div className="kpi">
                      <div className="kpi-label">Cash</div>
                      <div className="kpi-value">
                        ${totals.totalPrivateEventsCash.toFixed(2)}
                      </div>
                    </div>
                    <div className="kpi">
                      <div className="kpi-label">Card</div>
                      <div className="kpi-value">
                        ${totals.totalPrivateEventsCard.toFixed(2)}
                      </div>
                    </div>
                  </>
                )}
              </div>

              {/* Table */}
              <div className="data-box">
                <table className="data-table">
                  <thead>
                    {reportType === "transactions" ? (
                      <tr>
                        <th>#</th>
                        <th onClick={() => setSort("PName")} className="th-sort">
                          Parent Name
                        </th>
                        <th
                          onClick={() => setSort("PhoneNum")}
                          className="th-sort"
                        >
                          Phone Number
                        </th>
                        <th onClick={() => setSort("Date")} className="th-sort">
                          Date{" "}
                          {sortKey === "Date"
                            ? sortDir === "asc"
                              ? "▲"
                              : "▼"
                            : ""}
                        </th>
                        <th>Kid Count</th>
                        <th
                          onClick={() => setSort("PlayCost")}
                          className="th-sort"
                        >
                          Playtime Cost{" "}
                          {sortKey === "PlayCost"
                            ? sortDir === "asc"
                              ? "▲"
                              : "▼"
                            : ""}
                        </th>
                        <th>Food Orders</th>
                        <th
                          onClick={() => setSort("TotalAmount")}
                          className="th-sort"
                        >
                          Total Amount{" "}
                          {sortKey === "TotalAmount"
                            ? sortDir === "asc"
                              ? "▲"
                              : "▼"
                            : ""}
                        </th>
                      </tr>
                    ) : (
                      <tr>
                        <th>#</th>
                        <th
                          onClick={() => setSort("firstName")}
                          className="th-sort"
                        >
                          First Name
                        </th>
                        <th
                          onClick={() => setSort("lastName")}
                          className="th-sort"
                        >
                          Last Name
                        </th>
                        <th
                          onClick={() => setSort("phoneNumber")}
                          className="th-sort"
                        >
                          Phone
                        </th>
                        <th
                          onClick={() => setSort("startTime")}
                          className="th-sort"
                        >
                          Start{" "}
                          {sortKey === "startTime"
                            ? sortDir === "asc"
                              ? "▲"
                              : "▼"
                            : ""}
                        </th>
                        <th
                          onClick={() => setSort("endTime")}
                          className="th-sort"
                        >
                          End{" "}
                          {sortKey === "endTime"
                            ? sortDir === "asc"
                              ? "▲"
                              : "▼"
                            : ""}
                        </th>
                        <th>Kids</th>
                        <th>Guests</th>
                        <th>Package</th>
                        <th>Deposit</th>
                        <th>Paid in Full</th>
                        <th>Pay Method</th>
                        <th>Amount Paid</th>
                      </tr>
                    )}
                  </thead>

                  <tbody>
                    {sorted.map((item, index) =>
                      reportType === "transactions" ? (
                        <tr key={item.id || index}>
                          <td>{index + 1}</td>
                          <td>{item.PName}</td>
                          <td>{item.PhoneNum}</td>
                          <td>{item.Date}</td>
                          <td>
                            {item.KidCount ??
                              estimateKidCount(item.PlayCost || 0)}
                          </td>
                          <td>${Number(item.PlayCost || 0).toFixed(2)}</td>
                          <td>
                            {item.FoodOrder?.length
                              ? item.FoodOrder.map((o, i) => (
                                  <p key={i}>
                                    {o.food} (x{o.quantity})
                                  </p>
                                ))
                              : "N/A"}
                          </td>
                          <td>${Number(item.TotalAmount || 0).toFixed(2)}</td>
                        </tr>
                      ) : (
                        <tr
                          key={item.id || index}
                          className={
                            item.paidInFull ? "row-paid" : "row-unpaid"
                          }
                        >
                          <td>{index + 1}</td>
                          <td>{item.firstName}</td>
                          <td>{item.lastName}</td>
                          <td>{item.phoneNumber}</td>
                          <td>{new Date(item.startTime).toLocaleString()}</td>
                          <td>{new Date(item.endTime).toLocaleString()}</td>
                          <td>{item.numberOfKids}</td>
                          <td>{item.numberOfGuests}</td>
                          <td>{item.packageType}</td>
                          <td>${Number(item.depositAmount || 0).toFixed(2)}</td>
                          <td>
                            <button
                              className={`badge ${
                                item.paidInFull ? "badge-green" : "badge-red"
                              }`}
                              onClick={() =>
                                togglePaidStatus(item.id, item.paidInFull)
                              }
                              title="Toggle paid in full"
                            >
                              {item.paidInFull ? "Yes" : "No"}
                            </button>
                          </td>
                          <td>
                            <select
                              value={item.paymentMethod || ""}
                              onChange={(e) =>
                                updateEventPaymentMethod(item.id, e.target.value)
                              }
                            >
                              <option value="">Select</option>
                              <option value="Cash">Cash</option>
                              <option value="Card">Card</option>
                            </select>
                          </td>
                          <td>
                            <input
                              type="number"
                              step="0.01"
                              value={item.totalPaidAmount || 0}
                              onChange={(e) =>
                                updatePaidAmount(item.id, Number(e.target.value))
                              }
                              className="amount-input"
                            />
                          </td>
                        </tr>
                      )
                    )}
                  </tbody>

                  <tfoot>
                    {reportType === "transactions" ? (
                      <>
                        <tr>
                          <td colSpan="4">
                            <strong>Total Customers:</strong>{" "}
                            {totals.totalCustomers}
                          </td>
                          <td colSpan="4">
                            <strong>Total Kids:</strong> {totals.totalKids}
                          </td>
                        </tr>
                        <tr>
                          <td colSpan="3">
                            <strong>Total Cash:</strong> $
                            {totals.totalCash.toFixed(2)}
                          </td>
                          <td colSpan="3">
                            <strong>Total Card:</strong> $
                            {totals.totalCard.toFixed(2)}
                          </td>
                          <td colSpan="2">
                            <strong>Total Amount:</strong> $
                            {totals.totalAmount.toFixed(2)}
                          </td>
                        </tr>
                      </>
                    ) : (
                      <>
                        <tr>
                          <td colSpan="6">
                            <strong>Total Private Events:</strong>{" "}
                            {totals.totalPrivateEvents}
                          </td>
                          <td colSpan="6">
                            <strong>Total Amount (Deposit + Paid):</strong> $
                            {totals.totalPrivateEventsAmount.toFixed(2)}
                          </td>
                        </tr>
                        <tr>
                          <td colSpan="6">
                            <strong>Total Cash:</strong> $
                            {totals.totalPrivateEventsCash.toFixed(2)}
                          </td>
                          <td colSpan="6">
                            <strong>Total Card:</strong> $
                            {totals.totalPrivateEventsCard.toFixed(2)}
                          </td>
                        </tr>
                      </>
                    )}
                  </tfoot>
                </table>
              </div>
            </>
          ) : activeData.length > 0 ? (
            <p className="no-data-message">No results match your search.</p>
          ) : (
            <p className="no-data-message">
              No{" "}
              {reportType === "transactions" ? "transactions" : "private events"}{" "}
              found for the selected month and year.
            </p>
          )}
        </>
      )}

      {showTop && (
        <button
          className="back-to-top"
          onClick={scrollToTop}
          aria-label="Back to top"
          title="Back to top"
        >
          ↑
        </button>
      )}
    </div>
  );
};

export default MonthlyReport;
